// @flow

import React from 'react';
import { Translate } from 'react-redux-i18n';
import RoiParamField from './components/RoiParamField';

import type {
  CoqueRetailer,
  RoiBpParams,
  RoiInput,
  RoiParams,
  RoiResult,
  RoiSuggestionRequest,
} from '../../../types/apolloType';
import RoiPanelResult from './components/RoiPanelResult';
import storesApi from '../../../network/api/storesApi';
import pdf from '../../../resources/apollo_calculation_rules.pdf';
import ModalWrapper from '../../../components/ModalWrapper';
import RoiSuggestionResultDetails from './components/RoiSuggestionResultDetails';
import RoiPanelResultForm from './components/RoiPanelResultForm';
import RoiPanelBpResult from './components/RoiPanelBpResult';
import businessPlansApi from '../../../network/api/businessPlansApi';
import { getCurrentPcId } from '../../../utils/urlUtils';
import type { UrlParam } from '../../../utils/urlUtils';
import {
  computePayback,
  formatNaN,
  formatPayback,
} from '../../../utils/formatUtils';
import type { BusinessPlanBean } from '../../../types/geminiType';
import RoiBpResultDetails from './components/RoiBpResultDetails';
import {
  ESTIMATED_EXIT_CAP,
  ESTIMATED_LEASING_FEES,
  ESTIMATED_LOSS_OF_RENT,
  ESTIMATED_TRANSFER_TAX_AND_TRANSACTION_COSTS,
  ESTIMATED_VACANCY_CHARGES,
} from './RoiBpConst';

type Props = {
  roiResult: RoiResult,
  roiInput: RoiInput,
  codstr: string,
  codeRetailerProposition: string,
  addedCustomRetailer: CoqueRetailer,
  locationParams: UrlParam,
}

type State = {
  roiParams: RoiParams,
  mgrSuggestionResult: ?RoiResult,
  mgrSuggestionIsLoading: boolean,
  showCustomFocus: boolean,
  incentiveSuggestionResult: ?RoiResult,
  incentiveSuggestionIsLoading: boolean,
  showDetails: boolean,
  detailsModal: ?RoiResult,
  focus: string,
  businessPlan: BusinessPlanBean,
  roiBpParams: RoiBpParams,
}

type FocusableKpi = 'MGR' | 'INCENTIVES' | 'KEY_MONEY';

const euroPattern = '[0-9 ]+';
const decimalPattern = '[0-9 ]+(\\.[0-9]+)?';

const roiLabel = [
  { label: 'roi.kpi.roi', hideInfoBox: false },
  { label: 'roi.kpi.payback', hideInfoBox: false },
  { label: 'roi.kpi.newMgr', hideInfoBox: false },
  { label: 'roi.kpi.mgr', hideInfoBox: false },
  { label: 'roi.kpi.tic', hideInfoBox: false },
  { label: 'roi.kpi.keyMoney', hideInfoBox: false },
  { label: 'roi.kpi.valueCreationVsCurrent', hideInfoBox: false },
  { label: 'roi.kpi.Incentives', hideInfoBox: false },
];

const formatParams = (roiParams) => {
  const {
    newMgr,
    keyMoney,
    rentIncentives,
  } = roiParams;
  return {
    ...roiParams,
    newMgr: {
      max: Math.round(newMgr.max),
      min: Math.round(newMgr.min),
    },
    keyMoney: {
      max: Math.round(keyMoney.max),
      min: Math.round(keyMoney.min),
    },
    rentIncentives: {
      max: Math.round(rentIncentives.max),
      min: Math.round(rentIncentives.min),
    },
  };
};

const undoFormatParams = roiParams => ({ ...roiParams });

class RoiInterface extends React.Component<Props, State> {
  state = {
    roiParams: formatParams(this.props.roiResult.roiConstraints),
    mgrSuggestionResult: null,
    mgrSuggestionIsLoading: true,
    showCustomFocus: false,
    incentiveSuggestionResult: null,
    incentiveSuggestionIsLoading: true,
    showDetails: false,
    detailsModal: null,
    focus: null,
    businessPlan: null,
    roiBpParams: null,
  }

  getRoiBpParams = () => {
    // Default estimated value if there are not more accurate values
    let estimatedLeasingFees = ESTIMATED_LEASING_FEES;
    let estimatedLossOfRent = ESTIMATED_LOSS_OF_RENT;
    let estimatedVacancyCharges = ESTIMATED_VACANCY_CHARGES;
    let estimatedExitCap = ESTIMATED_EXIT_CAP;
    let estimatedTransferTaxAndTransactionCosts = ESTIMATED_TRANSFER_TAX_AND_TRANSACTION_COSTS;

    if (this.props.addedCustomRetailer) {
      const { roiParams } = this.props.addedCustomRetailer.roiResult;
      estimatedLeasingFees = formatNaN(roiParams.leasingFees);
      estimatedLossOfRent = formatNaN(roiParams.lossOfRent);
      estimatedVacancyCharges = formatNaN(roiParams.vacancyCharges);
      estimatedExitCap = formatNaN(roiParams.exitCap);
      estimatedTransferTaxAndTransactionCosts = formatNaN(roiParams.transferTaxAndTransactionCosts);
    }

    const businessPlan = this.state.businessPlan.tenant;
    const keyMoney = this.state.businessPlan.indemnities_paid;
    const evictionCost = this.state.businessPlan.indemnities_received;
    const foc = this.state.businessPlan.fitting_out_costs;
    const incentives = this.state.businessPlan.rent_incentives;
    const mgrUplift = this.state.businessPlan.mgr_uplift;

    const tic = keyMoney + evictionCost + foc + incentives + estimatedLeasingFees + estimatedLossOfRent + estimatedVacancyCharges;
    const roi = mgrUplift / tic;
    const valueCreationVsCurrent = (mgrUplift / (estimatedExitCap - tic)) / (1 + estimatedTransferTaxAndTransactionCosts);

    const payback = formatPayback(computePayback(mgrUplift, tic));
    const isMgrPositive = mgrUplift > 0;
    const newMgrGlaNet = this.state.businessPlan.new_mgr;
    const oldMgrGlaNet = this.state.businessPlan.old_mgr;

    const roiBpParams = {
      businessPlan,
      keyMoney,
      evictionCost,
      foc,
      incentives,
      estimatedLeasingFees,
      estimatedLossOfRent,
      estimatedVacancyCharges,
      mgrUplift,
      estimatedExitCap,
      estimatedTransferTaxAndTransactionCosts,
      tic,
      roi,
      valueCreationVsCurrent,
      payback,
      isMgrPositive,
      newMgrGlaNet,
      oldMgrGlaNet,
    };
    this.setState({ roiBpParams });
  }

  shouldComponentUpdate(nextProps: Props, nextState: State, nextContext: any): boolean {
    if (this.props.codeRetailerProposition !== nextProps.codeRetailerProposition) {
      nextState.roiParams = formatParams(nextProps.roiResult.roiConstraints);
    }
    return true;
  }

  computeRoiSuggestion = (
    roiSuggestionRequest: RoiSuggestionRequest,
    type: FocusableKpi,
    callback: (roiSuggestionResult: RoiResult) => {},
    onStart: () => {},
    onFinished: () => {},
  ) => {
    onStart();
    storesApi.computeRoiSuggestion(
      this.props.codstr,
      {
        ...roiSuggestionRequest,
        roiInput: this.props.roiInput,
      },
      type,
    )
      .then(resp => resp.json())
      .then((roiSuggestionResult) => {
        callback(roiSuggestionResult);
      })
      .catch(console.error)
      .finally(() => {
        onFinished();
      });
  };

  computeAllSuggestions = () => {
    const roiConstraints = undoFormatParams(this.state.roiParams);
    this.computeRoiSuggestion(
      {
        roiConstraints,
      },
      'MGR',
      roiSuggestionResult => this.setState({
        mgrSuggestionResult: roiSuggestionResult,
      }),
      () => this.setState({
        mgrSuggestionIsLoading: true,
      }),
      () => this.setState({
        mgrSuggestionIsLoading: false,
      }),
    );
    this.computeRoiSuggestion(
      {
        roiConstraints,
      },
      'INCENTIVES',
      roiSuggestionResult => this.setState({
        incentiveSuggestionResult: roiSuggestionResult,
      }),
      () => this.setState({
        incentiveSuggestionIsLoading: true,
      }),
      () => this.setState({
        incentiveSuggestionIsLoading: false,
      }),
    );
  }

  showDetails = (focus, roiSuggestionResult) => (show: boolean) => {
    this.setState({ showDetails: show });
    if (show) {
      this.setState({
        detailsModal: roiSuggestionResult,
        focus,
      });
    }
  };

  showDetailsResultForm = (focus: string) => (show: boolean, roiSuggestionResult: RoiResult) => {
    this.setState({ showDetails: show });
    if (show) {
      this.setState({
        detailsModal: roiSuggestionResult,
        focus,
      });
    }
  }

  onParamsChange = (key: string) => (value, minMax: string) => {
    const { roiParams } = this.state;
    roiParams[key][minMax] = value;
    this.setState({
      roiParams,
    });
  }

  componentDidMount() {
    this.computeAllSuggestions();

    businessPlansApi.fetchAllByPcId(getCurrentPcId())
      .then(response => response.json())
      .then((businessPlans) => {
        this.setState({
          businessPlan: businessPlans.filter(bp => bp.tenant === this.props.locationParams.bp)[0],
        });
      })
      .then(() => {
        if (this.props.locationParams.bp) {
          this.getRoiBpParams();
        }
      });
  }

  onSubmit = (e) => {
    e.preventDefault();
    this.computeAllSuggestions();
    this.setState({
      showCustomFocus: true,
    });
  }

  render() {
    const {
      roiParams,
      mgrSuggestionResult,
      mgrSuggestionIsLoading,
      incentiveSuggestionResult,
      incentiveSuggestionIsLoading,
      showDetails,
      detailsModal,
      focus,
    } = this.state;
    const isLoading = mgrSuggestionIsLoading
      || incentiveSuggestionIsLoading;
    return (
      <div className="roi-interface">
        <div className="roi-row">
          <div className="roi-panel roi-panel-params">
            <h3 className="roi-subtitle"><Translate value="roi.params.title" /></h3>
            <div className="roi-panel-content">
              <Translate className="form-description" value="roi.params.description" />
              <form onSubmit={this.onSubmit} className="roi-inputs-container">
                <div className="roi-param-field-container">
                  <div className="roi-category">
                    <div className="roi-label roi-label--oldMgr">
                      <Translate
                        value="roi.kpi.oldMgr"
                        amount={`${Math.round(this.props.roiResult.roiParams.oldMgrGlaNet / 1000)} K €`}
                      />
                    </div>
                    <div className="roi-param-field">
                      <div className="roi-label-category roi-label">
                        <Translate value="roi.kpi.newMgr" />
                      </div>
                      <div className="roi-label roi-label--start-min">
                        <Translate value="roi.params.min" />
                      </div>
                      <div className="roi-label roi-label--start-max">
                        <Translate value="roi.params.max" />
                      </div>
                    </div>
                    <RoiParamField
                      label={<Translate value="roi.params.newMgr" />}
                      value={roiParams.newMgr}
                      onChange={this.onParamsChange('newMgr')}
                      unit="€"
                      pattern={euroPattern}
                    />
                    <RoiParamField
                      label={<Translate value="roi.params.leasingFeesOnNewMgr" />}
                      value={roiParams.leasingFeesOnNewMgr}
                      onChange={this.onParamsChange('leasingFeesOnNewMgr')}
                      unit="%"
                      pattern={decimalPattern}
                      showField={false}
                    />
                    <RoiParamField
                      label={<Translate value="roi.params.leasingFeesOnUplift" />}
                      value={roiParams.leasingFeesOnUplift}
                      onChange={this.onParamsChange('leasingFeesOnUplift')}
                      unit="%"
                      pattern={decimalPattern}
                      showField={false}
                    />
                  </div>
                  <div className="roi-category">
                    <div className="roi-param-field">
                      <div className="roi-label-category roi-label">
                        <Translate value="roi.kpi.keyMoney" />
                      </div>
                      <div className="roi-label roi-label--start" />
                      <div className="roi-label roi-label--start" />
                    </div>
                    <RoiParamField
                      label={<Translate value="roi.params.keyMoney" />}
                      value={roiParams.keyMoney}
                      unit="€"
                      onChange={this.onParamsChange('keyMoney')}
                      pattern={euroPattern}
                    />
                    <RoiParamField
                      label={<Translate value="roi.params.evictionCost" />}
                      value={roiParams.evictionCost}
                      unit="€"
                      onChange={this.onParamsChange('evictionCost')}
                      pattern={euroPattern}
                      showField={false}
                    />
                    <RoiParamField
                      label={<Translate value="roi.params.foc" />}
                      value={roiParams.foc}
                      onChange={this.onParamsChange('foc')}
                      unit="€"
                      pattern={euroPattern}
                      showField={false}
                    />
                  </div>
                  <div className="roi-category">
                    <div className="roi-param-field">
                      <div className="roi-label-category roi-label">
                        <Translate value="roi.category.incentives" />
                      </div>
                      <div className="roi-label roi-label--start" />
                      <div className="roi-label roi-label--start" />
                    </div>
                    <RoiParamField
                      label={<Translate value="roi.params.rentIncentives" />}
                      value={roiParams.rentIncentives}
                      unit="€"
                      onChange={this.onParamsChange('rentIncentives')}
                      pattern={euroPattern}
                    />
                  </div>
                </div>
                <div className="roi-button-container">
                  <button
                    type="submit"
                    className="apollo-button-contained"
                    disabled={isLoading}
                  >
                    <div className="button-big-text">
                      {
                        isLoading ? (
                          <i className="fa fa-spinner fa-spin" />
                        ) : (
                          <Translate value="roi.compute" />
                        )
                      }
                    </div>
                  </button>
                </div>
              </form>
            </div>
          </div>
          {
            showDetails
            && (
              (this.props.locationParams.bp && this.state.focus === 'roi')
                ? (
                  <ModalWrapper
                    title={<Translate value="roi.bpfocus" bp={`(${this.props.locationParams.bp})`} />}
                    showModal={this.showDetails(detailsModal)}
                    content={(<RoiBpResultDetails
                      businessPlan={this.state.roiBpParams}
                    />)}
                    className="roi-details-modal"
                  />
                )
                : (
                  <ModalWrapper
                    title={<Translate value={`roi.${focus}focus`} />}
                    showModal={this.showDetails(detailsModal)}
                    content={(<RoiSuggestionResultDetails
                      roiSuggestionResult={detailsModal}
                      focus={focus}
                    />)}
                    className="roi-details-modal"
                  />
                )

            )
          }
          <div className="roi-kpi-label-container">
            {
              roiLabel.map(roiLabelItem => (
                <div className="roi-kpi-label-item" key={roiLabelItem.label}>
                  <div className="roi-kpi-label roi-label"><Translate value={roiLabelItem.label} /></div>
                  {
                    !roiLabelItem.hideInfoBox && (
                      <a target="_blank" href={pdf} rel="noopener noreferrer"><i className="fa fa-info-circle" /></a>
                    )
                  }
                </div>
              ))
            }
          </div>
          <div className="roi-panels-result">
            <RoiPanelResult
              title="roi.mgrfocus"
              titleDetails="roi.mgrfocus_details"
              resultLoading={mgrSuggestionIsLoading}
              roiSuggestionResult={mgrSuggestionResult}
              focus="mgr"
              showDetails={this.showDetails('mgr', mgrSuggestionResult)}
            />
            <RoiPanelResult
              title="roi.incentivesfocus"
              titleDetails="roi.incentivesfocus_details"
              resultLoading={incentiveSuggestionIsLoading}
              roiSuggestionResult={incentiveSuggestionResult}
              focus="incentives"
              showDetails={this.showDetails('incentives', incentiveSuggestionResult)}
            />
            {
              this.props.locationParams.bp && (
                <RoiPanelBpResult
                  title={<Translate
                    value="roi.bpfocus"
                    bp={this.props.locationParams.bp ? `(${this.props.locationParams.bp})` : ''}
                  />}
                  titleDetails="roi.bpfocus_details"
                  resultLoading={mgrSuggestionIsLoading}
                  businessPlan={this.state.roiBpParams}
                  focus="roi"
                  showDetails={this.showDetails('roi', mgrSuggestionResult)}
                />
              )
            }
            {
              this.state.showCustomFocus
              && (
                mgrSuggestionIsLoading ?
                  (<RoiPanelResult
                    title="roi.mgrfocusform"
                    titleDetails="roi.mgrfocusform_details"
                    resultLoading={mgrSuggestionIsLoading}
                    roiSuggestionResult={mgrSuggestionResult}
                    focus="mgr"
                    showDetails={this.showDetails('mgr', mgrSuggestionResult)}
                  />)
                  : (<RoiPanelResultForm
                      title="roi.mgrfocusform"
                      titleDetails="roi.mgrfocusform_details"
                      resultLoading={mgrSuggestionIsLoading}
                      roiSuggestionResult={mgrSuggestionResult}
                      roiConstraints={roiParams}
                      showDetails={this.showDetailsResultForm('mgr')}
                      codstr={this.props.codstr}
                  />)
              )
            }
          </div>
        </div>
      </div>
    );
  }
}

export default RoiInterface;
